import { useMemo, useRef } from 'react'

type BackdropEvent = {
	target: EventTarget | null
	currentTarget: EventTarget | null
}

export type BackdropCloseHandlers = {
	onMouseDown: (e: BackdropEvent) => void
	onMouseUp: (e: BackdropEvent) => void
	onClick: (e: BackdropEvent) => void
}

const onBackdrop = (e: BackdropEvent): boolean => e.target === e.currentTarget

/**
 * Click handlers for a modal's backdrop. It closes only when the press and the
 * release both land on the backdrop itself. A drag between the content and the
 * backdrop (selecting text and letting go outside, or the other way round)
 * still fires its click on the backdrop, and must not close.
 */
export const createBackdropClose = (
	close: () => void
): BackdropCloseHandlers => {
	let pressed = false
	let released = false
	return {
		onMouseDown: e => {
			pressed = onBackdrop(e)
			released = false
		},
		onMouseUp: e => {
			released = onBackdrop(e)
		},
		onClick: e => {
			const closes = pressed && released && onBackdrop(e)
			pressed = false
			released = false
			if (closes) close()
		},
	}
}

export const useBackdropClose = (
	onClose?: () => unknown
): BackdropCloseHandlers => {
	const onCloseRef = useRef(onClose)
	onCloseRef.current = onClose
	return useMemo(() => createBackdropClose(() => onCloseRef.current?.()), [])
}
